import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod";

export const env = createEnv({
  /*
   * Serverside Environment variables, not available on the client.
   * Will throw if you access these variables on the client.
   */
  server: {
    NODE_ENV: z.string().optional(),
    CONTENTFUL_ENDPOINT: z.string().nonempty(),
    CONTENTFUL_ACCESS_TOKEN: z.string().nonempty(),
    CONTENTFUL_PREVIEW: z.boolean({
      description:
        "By default, Contentful queries do not return draft content. If you would like to override this behaviour, enable this flag. Needs to be used in conjunction with a CONTENTFUL_ACCESS_TOKEN with preview access.",
    }),
    CANONICAL_URL_HOST: z.string().nonempty().default("www.redbull.com"),
    HEROKU_SLUG_COMMIT: z
      .string({
        description: `This comes from “Dyno Metadata” on Heroku, see: https://devcenter.heroku.com/articles/dyno-metadata. It's not needed outside the context of Heroku, which is why it's marked as .optional()`,
      })
      .optional(),
    CREPO_GRAPHQL_ENDPOINT: z.string().nonempty(),
    CREPO_GRAPHQL_API_KEY: z.string().nonempty(),
    STORYBLOK_PREVIEW_ACCESS_TOKEN: z
      .string({
        description:
          "Retrieved from: https://app.storyblok.com/#/me/spaces/287059/settings?tab=api",
      })
      .nonempty(),
    STORYBLOK_DRAFTS_ENABLED: z.enum(["true", "false"]).default("false"),
    STORYBLOK_STAGING_ENABLED: z.enum(["true", "false"]).default("false"),

    /**
     * On staging (but not in production) we want to expose the internal
     * `/storyblok/` prefixed routes, so that EXA can preview their work on
     * routes that are otherwise resolved to `/contentful/` paths via rewrites,
     * such as sustainability pages and company pages which are on a different
     * release schedule to each locale's product pages.
     */
    CONTENTFUL_PREFIXED_ROUTES_ENABLED: z
      .enum(["true", "false"])
      .default("false"),
  },
  /*
   * Environment variables available on the client (and server).
   *
   * 💡 You'll get type errors if these are not prefixed with NEXT_PUBLIC_.
   */
  client: {
    NEXT_PUBLIC_HAS_GTM_CONTAINER: z.boolean({
      description:
        "This should be “true” in environments where GTM should be enabled (ie. non-development environments)",
    }),
    NEXT_PUBLIC_ASSET_PREFIX: z.string({
      description:
        "Has to be used as `assetPrefix` in next.config.js because this application does not live at the domain root in production. As such, we also need to prefix all asset paths with it.",
    }),
    NEXT_PUBLIC_RBGEMC_ACCOUNTS_ENV: z
      .enum(["design", "production"])
      .default("design"),
    NEXT_PUBLIC_RBGEMC_ACCOUNTS_BUNDLE: z
      .enum([
        "https://stage-account.redbull.com/sdk/rba-lightbox.min.js",
        "https://account.redbull.com/sdk/rba-lightbox.min.js",
      ])
      .default("https://stage-account.redbull.com/sdk/rba-lightbox.min.js"),
    NEXT_PUBLIC_ZENDESK_ENV: z
      .enum(["production", "staging", "development"])
      .default("development"),
  },
  /*
   * Due to how Next.js bundles environment variables on Edge and Client,
   * we need to manually destructure them to make sure all are included in bundle.
   *
   * 💡 You'll get type errors if not all variables from `server` & `client` are included here.
   */
  runtimeEnv: {
    /* eslint-disable no-restricted-properties */
    NODE_ENV: process.env.NODE_ENV,
    NEXT_PUBLIC_HAS_GTM_CONTAINER:
      process.env.NEXT_PUBLIC_HAS_GTM_CONTAINER === "true",
    CONTENTFUL_ENDPOINT: process.env.CONTENTFUL_ENDPOINT,
    CONTENTFUL_ACCESS_TOKEN: process.env.CONTENTFUL_ACCESS_TOKEN,
    CONTENTFUL_PREVIEW: process.env.CONTENTFUL_PREVIEW === "true",
    CANONICAL_URL_HOST: process.env.CANONICAL_URL_HOST,
    HEROKU_SLUG_COMMIT: process.env.HEROKU_SLUG_COMMIT,
    NEXT_PUBLIC_ASSET_PREFIX: process.env.NEXT_PUBLIC_ASSET_PREFIX,
    CREPO_GRAPHQL_ENDPOINT: process.env.CREPO_GRAPHQL_ENDPOINT,
    CREPO_GRAPHQL_API_KEY: process.env.CREPO_GRAPHQL_API_KEY,
    STORYBLOK_PREVIEW_ACCESS_TOKEN: process.env.STORYBLOK_PREVIEW_ACCESS_TOKEN,
    STORYBLOK_DRAFTS_ENABLED: process.env.STORYBLOK_DRAFTS_ENABLED,
    STORYBLOK_STAGING_ENABLED: process.env.STORYBLOK_STAGING_ENABLED,
    NEXT_PUBLIC_RBGEMC_ACCOUNTS_ENV:
      process.env.NEXT_PUBLIC_RBGEMC_ACCOUNTS_ENV,
    NEXT_PUBLIC_RBGEMC_ACCOUNTS_BUNDLE:
      process.env.NEXT_PUBLIC_RBGEMC_ACCOUNTS_BUNDLE,
    NEXT_PUBLIC_ZENDESK_ENV: process.env.NEXT_PUBLIC_ZENDESK_ENV,
    CONTENTFUL_PREFIXED_ROUTES_ENABLED:
      process.env.CONTENTFUL_PREFIXED_ROUTES_ENABLED,
    /* eslint-enable no-restricted-properties */
  },
});
